// src/stores/useExerciseStore.ts
// Exercise session state: current unit/section/exercise, score, history, and
// the cached UnitData/progressData/unitMediaCache for the active book.
//
// All scoring and JSON conversion lives in ../services/exerciseService.ts.
// All progress persistence goes through progressSync.ts (built in Session 2).
// Access checks go through paymentService.ts (built in Session 4).
//
// Logout is blocked while `isExerciseActive` is true on useAuthStore — set
// to true the moment a unit starts, false on closeSession().

import { create } from 'zustand';
import type { ProgressSync } from '../services/progressSync';
import { progressSync } from '../services/progressSync';
import {
  calculateMaxScore,
  findExercise,
  findSection,
  getUnitData,
  gradeExercise,
} from '../services/exerciseService';
import { canAccessUnit } from '../services/paymentService';
import { useAuthStore } from './useAuthStore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase';
import type {
  AnswerRecord,
  Exercise,
  ExerciseUserInput,
  GradeResult,
  SectionId,
  UnitData,
} from '../types/exercise';
import type { AllProgress, SectionProgress, UnitProgress } from '../types/progress';

// ── Module-scope helpers (not part of state, never re-render) ────────────────

/** Debounced save timer for non-immediate saveProgress() calls. */
let _saveDebounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Per-section JSON-hash cache so we never write the same section twice in a
 * row. Mirrors the old ExerciseManager `syncQueue` Map. Keyed by
 * `${scopedKey}_${sectionId}`. Cleared by `_resetSaveCache`.
 */
const _saveHashCache = new Map<string, string>();

const _scopedProgressKey = (bookId: string, unitId: string): string =>
  `${bookId}_${unitId}`;

function _resetSaveCache(): void {
  if (_saveDebounceTimer) {
    clearTimeout(_saveDebounceTimer);
    _saveDebounceTimer = null;
  }
  _saveHashCache.clear();
}

// ── Public types ─────────────────────────────────────────────────────────────

export type StartUnitResult =
  | { ok: true }
  | { ok: false; reason: 'login_required' | 'access_denied' | 'load_failed'; message: string };

// ── Store interface ──────────────────────────────────────────────────────────

interface ExerciseState {
  // ── Current session ─────────────────────────────────────────────────────
  currentBookId: string | null;
  /** "unit3" form. Used as in-memory key. Scoped to bookId for IDB. */
  currentUnitId: string | null;
  currentSectionId: SectionId | null;
  currentExerciseIndex: number;
  score: number;
  wrongCount: number;
  totalExercises: number;
  /** Indices already submitted in this section — re-submits don't double-count. */
  answeredIndices: Set<number>;
  /** Per-question records (used by completion screen review list). */
  answerHistory: AnswerRecord[];
  /** True while `startUnit` is in flight. UI should show a loader. */
  isLoading: boolean;
  /** True when nextExercise() ran past the last exercise — UI shows completion. */
  isCompletion: boolean;

  // ── Cached data ─────────────────────────────────────────────────────────
  /** unitId → UnitData (parsed sections). Per-session, not persisted. */
  exerciseData: Record<string, UnitData>;
  /** unitId → UnitProgress. Read from progressSync on startUnit. */
  progressData: AllProgress;
  /** unitId → media URLs. Populated when book loads. */
  unitMediaCache: Record<string, { audioUrl?: string; videoUrl?: string }>;

  // ── Service handles (exposed for hooks) ─────────────────────────────────
  progressSync: ProgressSync;

  // ── Actions ─────────────────────────────────────────────────────────────
  startUnit: (unitId: string, bookId: string) => Promise<StartUnitResult>;
  startSection: (sectionId: SectionId) => boolean;
  submitAnswer: (input: ExerciseUserInput) => GradeResult | null;
  nextExercise: () => void;
  prevExercise: () => void;
  /** "Practice Again" on completion screen — soft reset of in-memory + IDB. */
  restartSection: () => Promise<void>;
  /** Return from active section (or completion) to the section overview. */
  returnToOverview: () => Promise<void>;
  /** Close the entire exercise session, return to homepage. */
  closeSession: () => Promise<void>;
  /** Section overview "Reset" button — full IDB+Firestore force-reset with resetAt stamp. */
  resetSection: (sectionId: SectionId) => Promise<void>;
  /** Persist current `progressData[currentUnit]` via progressSync. Debounced 300ms. */
  saveProgress: (forceImmediate?: boolean) => Promise<void>;

  // ── Internal helpers (called by actions, also exposed for tests) ────────
  /** Re-read progress from IDB/cloud into store state. */
  _loadProgressFromIDB: () => Promise<void>;
  /** Update progressData[unit][section] from in-memory score after each submit. */
  _updateSectionProgress: () => void;
}

// ── Initial state ────────────────────────────────────────────────────────────

const INITIAL_SESSION = {
  currentSectionId: null as SectionId | null,
  currentExerciseIndex: 0,
  score: 0,
  wrongCount: 0,
  totalExercises: 0,
  answeredIndices: new Set<number>(),
  answerHistory: [] as AnswerRecord[],
  isCompletion: false,
};

// ── Store ────────────────────────────────────────────────────────────────────

export const useExerciseStore = create<ExerciseState>()((set, get) => ({
  currentBookId: null,
  currentUnitId: null,
  ...INITIAL_SESSION,
  isLoading: false,
  exerciseData: {},
  progressData: {},
  unitMediaCache: {},
  progressSync,

  // ── startUnit ───────────────────────────────────────────────────────────
  startUnit: async (unitId, bookId) => {
    if (!unitId || !unitId.startsWith('unit')) {
      return { ok: false, reason: 'load_failed', message: `Invalid unit ID: ${unitId}` };
    }
    if (!bookId) {
      return { ok: false, reason: 'load_failed', message: 'Missing bookId' };
    }
    if (get().isLoading) {
      // Cheap guard against double-clicks.
      return { ok: false, reason: 'load_failed', message: 'Already loading' };
    }

    // 1. Auth gate — anonymous and signed-out users cannot enter a unit.
    const user = auth.currentUser;
    if (!user || user.isAnonymous) {
      return {
        ok: false,
        reason: 'login_required',
        message: 'Please sign in to access exercises.',
      };
    }

    // 2. Subscription / free-unit gate.
    let hasAccess = false;
    try {
      hasAccess = await canAccessUnit(bookId, unitId);
    } catch (err) {
      console.warn('[useExerciseStore] canAccessUnit threw:', err);
      return {
        ok: false,
        reason: 'load_failed',
        message: 'Could not verify subscription. Please refresh.',
      };
    }
    if (!hasAccess) {
      return {
        ok: false,
        reason: 'access_denied',
        message: 'This unit requires an active subscription.',
      };
    }

    // 3. Block logout for the duration of the session.
    useAuthStore.getState().setIsExerciseActive(true);

    set({
      isLoading: true,
      currentBookId: bookId,
      currentUnitId: unitId,
      ...INITIAL_SESSION,
    });
    _resetSaveCache();

    try {
      // 4. Load + cache UnitData (or reuse cache).
      let unit = get().exerciseData[unitId];
      let media: { audioUrl?: string; videoUrl?: string } | undefined = get().unitMediaCache[unitId];

      const isSameBook = get().currentBookId === bookId;
      if (!unit || !isSameBook) {
        const result = await getUnitData(bookId, unitId);
        unit = result.unitData;
        media = result.unitMedia ?? undefined;
        set((s) => ({
          exerciseData: { ...s.exerciseData, [unitId]: unit! },
          unitMediaCache: media
            ? { ...s.unitMediaCache, [unitId]: media }
            : s.unitMediaCache,
        }));
      }

      // 5. Load progress for this unit (IDB → cloud → empty).
      await get()._loadProgressFromIDB();

      // 6. Best-effort: flush any pending writes.
      progressSync.flushQueue().catch((e) => {
        console.warn('[useExerciseStore] post-load flush failed:', e);
      });

      set({ isLoading: false });
      return { ok: true };
    } catch (err) {
      console.error('[useExerciseStore] startUnit failed:', err);
      // Roll back the active flag if load failed before the user could start.
      useAuthStore.getState().setIsExerciseActive(false);
      set({
        isLoading: false,
        currentBookId: null,
        currentUnitId: null,
      });
      return {
        ok: false,
        reason: 'load_failed',
        message: err instanceof Error ? err.message : 'Failed to load unit',
      };
    }
  },

  // ── startSection ────────────────────────────────────────────────────────
  startSection: (sectionId) => {
    const { currentUnitId, currentBookId, exerciseData } = get();
    if (!currentUnitId || !currentBookId) {
      console.error('[useExerciseStore] startSection: no current unit');
      return false;
    }
    const unit = exerciseData[currentUnitId];
    if (!unit) {
      console.error('[useExerciseStore] startSection: no exercise data');
      return false;
    }
    const section = findSection(unit, sectionId);
    if (!section || section.exercises.length === 0) {
      console.error(`[useExerciseStore] startSection: section ${sectionId} empty or missing`);
      return false;
    }

    // Initialize / upgrade progressData[unit][section] before the user starts.
    // Old code preserved existing fields and refreshed `total` / `lastUpdated`.
    const scopedKey = _scopedProgressKey(currentBookId, currentUnitId);
    set((s) => {
      const unitProgress: UnitProgress = { ...(s.progressData[scopedKey] || {}) };
      const existing = unitProgress[sectionId] || ({} as Partial<SectionProgress>);
      unitProgress[sectionId] = {
        completed: existing.completed ?? 0,
        total: section.exercises.length,
        score: existing.score ?? existing.completed ?? 0,
        maxScore: section.exercises.length,
        percentage: existing.percentage ?? 0,
        version: existing.version ?? 0,
        lastUpdatedMs: existing.lastUpdatedMs ?? 0,
        lastUpdated: existing.lastUpdated ?? new Date().toISOString(),
        completedFlag: existing.completedFlag,
        pendingSync: existing.pendingSync,
        resetAt: existing.resetAt,
      };
      return {
        currentSectionId: sectionId,
        currentExerciseIndex: 0,
        score: 0,
        wrongCount: 0,
        totalExercises: section.exercises.length,
        answeredIndices: new Set<number>(),
        answerHistory: [],
        isCompletion: false,
        progressData: { ...s.progressData, [scopedKey]: unitProgress },
      };
    });

    return true;
  },

  // ── submitAnswer ────────────────────────────────────────────────────────
  submitAnswer: (input) => {
    const state = get();
    const { currentUnitId, currentSectionId, currentExerciseIndex, exerciseData } = state;
    if (!currentUnitId || !currentSectionId) return null;

    const unit = exerciseData[currentUnitId];
    if (!unit) return null;

    const exercise: Exercise | undefined = findExercise(
      unit,
      currentSectionId,
      currentExerciseIndex,
    );
    if (!exercise) return null;

    const grade = gradeExercise(exercise, input);
    const alreadyAnswered = state.answeredIndices.has(currentExerciseIndex);

    // Build the history entry. ALWAYS upsert by index — re-submitting after
    // Previous overwrites, never duplicates (matches old answerHistory
    // findIndex-then-replace pattern).
    const historyEntry: AnswerRecord = {
      index: currentExerciseIndex,
      type: exercise.type,
      question: exercise.question || '—',
      userAnswerDisplay: grade.userAnswerDisplay,
      correctAnswerDisplay: grade.correctAnswerDisplay,
      isCorrect: grade.isCorrect,
      scoreEarned: grade.scoreEarned,
      maxScore: calculateMaxScore(exercise),
    };
    const existingHistoryIdx = state.answerHistory.findIndex(
      (h) => h.index === currentExerciseIndex,
    );
    const newHistory = [...state.answerHistory];
    if (existingHistoryIdx !== -1) newHistory[existingHistoryIdx] = historyEntry;
    else newHistory.push(historyEntry);

    if (alreadyAnswered) {
      // Don't update score or wrongCount — only refresh the review entry.
      // This matches the old `answeredExercises` Set guard exactly.
      set({ answerHistory: newHistory });
      return grade;
    }

    const newAnswered = new Set(state.answeredIndices);
    newAnswered.add(currentExerciseIndex);
    const newScore = state.score + grade.scoreEarned;
    const newWrong = state.wrongCount + (grade.isCorrect ? 0 : 1);

    set({
      score: newScore,
      wrongCount: newWrong,
      answeredIndices: newAnswered,
      answerHistory: newHistory,
    });

    // Real-time progress update + debounced save.
    get()._updateSectionProgress();
    void get().saveProgress();

    return grade;
  },

  // ── nextExercise ────────────────────────────────────────────────────────
  nextExercise: () => {
    const { currentUnitId, currentSectionId, currentExerciseIndex, exerciseData } = get();
    if (!currentUnitId || !currentSectionId) return;
    const unit = exerciseData[currentUnitId];
    if (!unit) return;
    const section = findSection(unit, currentSectionId);
    if (!section) return;

    const nextIndex = currentExerciseIndex + 1;
    if (nextIndex >= section.exercises.length) {
      // Section complete — flip into the completion view and finalize progress.
      set({ isCompletion: true });
      _finalizeSectionProgress();
    } else {
      set({ currentExerciseIndex: nextIndex });
    }
  },

  // ── prevExercise ────────────────────────────────────────────────────────
  prevExercise: () => {
    const { currentExerciseIndex } = get();
    if (currentExerciseIndex > 0) {
      set({ currentExerciseIndex: currentExerciseIndex - 1 });
    }
  },

  // ── restartSection (Practice Again on completion screen) ────────────────
  restartSection: async () => {
    const { currentBookId, currentUnitId, currentSectionId, totalExercises } = get();
    if (!currentBookId || !currentUnitId || !currentSectionId) return;

    // Soft reset — no `resetAt` stamp (that's only for explicit user resets).
    const scopedKey = _scopedProgressKey(currentBookId, currentUnitId);
    const softResetData: Partial<SectionProgress> = {
      completed: 0,
      total: totalExercises,
      score: 0,
      maxScore: totalExercises,
      percentage: 0,
      pendingSync: true,
    };
    try {
      await progressSync.saveProgress(scopedKey, currentSectionId, softResetData);
    } catch (e) {
      console.warn('[useExerciseStore] restartSection: IDB write failed:', e);
    }
    // Clear the hash cache for this section so the next save isn't deduped away.
    _saveHashCache.delete(`${scopedKey}_${currentSectionId}`);

    // Reset in-memory session counters.
    set((s) => {
      const unitProgress: UnitProgress = { ...(s.progressData[scopedKey] || {}) };
      delete unitProgress[currentSectionId];
      return {
        currentExerciseIndex: 0,
        score: 0,
        wrongCount: 0,
        answeredIndices: new Set<number>(),
        answerHistory: [],
        isCompletion: false,
        progressData: { ...s.progressData, [scopedKey]: unitProgress },
      };
    });
  },

  // ── returnToOverview ────────────────────────────────────────────────────
  returnToOverview: async () => {
    // Force-flush before navigating so the section overview shows fresh rings.
    await get().saveProgress(true);
    set({
      currentSectionId: null,
      currentExerciseIndex: 0,
      score: 0,
      wrongCount: 0,
      totalExercises: 0,
      answeredIndices: new Set<number>(),
      answerHistory: [],
      isCompletion: false,
    });
  },

  // ── closeSession ────────────────────────────────────────────────────────
  closeSession: async () => {
    // Fire the save without blocking — doSave() captures `get()` synchronously
    // before the state reset below, so all progress data is still available.
    void get().saveProgress(true);
    useAuthStore.getState().setIsExerciseActive(false);
    _resetSaveCache();
    set({
      currentBookId: null,
      currentUnitId: null,
      currentSectionId: null,
      currentExerciseIndex: 0,
      score: 0,
      wrongCount: 0,
      totalExercises: 0,
      answeredIndices: new Set<number>(),
      answerHistory: [],
      isLoading: false,
      isCompletion: false,
      // exerciseData / progressData / unitMediaCache kept across sessions so
      // re-entering a unit doesn't re-fetch. Cleared on logout via clearStore.
    });
  },

  // ── resetSection (overview "Reset" button — full force-reset) ───────────
  resetSection: async (sectionId) => {
    const { currentBookId, currentUnitId, progressData } = get();
    if (!currentBookId || !currentUnitId) return;

    const scopedKey = _scopedProgressKey(currentBookId, currentUnitId);
    const unitProgress = progressData[scopedKey] || {};
    const existing = unitProgress[sectionId];
    if (!existing || (existing.completed ?? 0) === 0) {
      console.log('[useExerciseStore] resetSection: nothing to reset');
      return;
    }

    const resetNow = Date.now();
    const resetData: Partial<SectionProgress> = {
      completed: 0,
      total: existing.total ?? 0,
      score: 0,
      maxScore: existing.maxScore ?? existing.total ?? 0,
      percentage: 0,
      lastUpdated: new Date(resetNow).toISOString(),
      lastUpdatedMs: resetNow,
      completedFlag: false,
      // resetAt makes mergeSection's RESET-WINS branch fire — stale listener
      // events with the old percentage cannot un-do this reset.
      resetAt: resetNow,
      // Bump version too so the version tiebreaker also favors the reset.
      version: (existing.version ?? 0) + 1,
    };

    _saveHashCache.delete(`${scopedKey}_${sectionId}`);
    try {
      // force:true bypasses the no-downgrade guard inside progressSync.
      await progressSync.saveProgress(scopedKey, sectionId, resetData, { force: true });
    } catch (e) {
      console.warn('[useExerciseStore] resetSection: IDB write failed:', e);
    }

    set((s) => {
      const u: UnitProgress = { ...(s.progressData[scopedKey] || {}) };
      // Use sanitized full record so the UI ring updates immediately.
      u[sectionId] = {
        completed: 0,
        total: existing.total ?? 0,
        score: 0,
        maxScore: existing.maxScore ?? existing.total ?? 0,
        percentage: 0,
        version: (existing.version ?? 0) + 1,
        lastUpdated: new Date(resetNow).toISOString(),
        lastUpdatedMs: resetNow,
        completedFlag: false,
        pendingSync: false,
        resetAt: resetNow,
      };
      return { progressData: { ...s.progressData, [scopedKey]: u } };
    });
  },

  // ── saveProgress (debounced 300ms unless forceImmediate) ────────────────
  saveProgress: async (forceImmediate = false) => {
    if (useAuthStore.getState().isLoggingOut) {
      console.log('[useExerciseStore] saveProgress skipped: logout in progress');
      return;
    }
    const { currentBookId, currentUnitId } = get();
    if (!currentBookId || !currentUnitId) return;

    if (_saveDebounceTimer) {
      clearTimeout(_saveDebounceTimer);
      _saveDebounceTimer = null;
    }

    const doSave = async (): Promise<void> => {
      // Re-read state at flush time so debounced batches pick up latest data.
      const s = get();
      if (!s.currentBookId || !s.currentUnitId) return;
      const scopedKey = _scopedProgressKey(s.currentBookId, s.currentUnitId);
      const unitProgress = s.progressData[scopedKey] || {};

      for (const [sectionId, section] of Object.entries(unitProgress)) {
        const hash = JSON.stringify(section);
        const cacheKey = `${scopedKey}_${sectionId}`;
        if (_saveHashCache.get(cacheKey) === hash) continue;
        try {
          await progressSync.saveProgress(scopedKey, sectionId, section);
          _saveHashCache.set(cacheKey, hash);
        } catch (e) {
          console.warn(`[useExerciseStore] saveProgress(${sectionId}) failed:`, e);
        }
      }
    };

    if (forceImmediate) {
      await doSave();
    } else {
      _saveDebounceTimer = setTimeout(() => {
        _saveDebounceTimer = null;
        void doSave();
      }, 300);
    }
  },

  // ── _loadProgressFromIDB ────────────────────────────────────────────────
  _loadProgressFromIDB: async () => {
    const { currentBookId, currentUnitId } = get();
    if (!currentBookId || !currentUnitId) return;
    const scopedKey = _scopedProgressKey(currentBookId, currentUnitId);
    try {
      const unitProgress = await progressSync.loadProgress(scopedKey);
      set((s) => ({
        progressData: {
          ...s.progressData,
          [scopedKey]: unitProgress || {},
        },
      }));
    } catch (e) {
      console.warn('[useExerciseStore] _loadProgressFromIDB failed:', e);
      set((s) => ({
        progressData: { ...s.progressData, [scopedKey]: {} },
      }));
    }
  },

  // ── _updateSectionProgress (real-time progress during a section) ────────
  _updateSectionProgress: () => {
    const state = get();
    const { currentBookId, currentUnitId, currentSectionId, score, totalExercises } = state;
    if (!currentBookId || !currentUnitId || !currentSectionId) return;

    const scopedKey = _scopedProgressKey(currentBookId, currentUnitId);
    const total = totalExercises || 0;
    // Cap at 100 — a sanity net against any data races inflating the value.
    const rawPct = total > 0 ? Math.round((score / total) * 100) : 0;
    const pct = Math.min(100, rawPct);

    set((s) => {
      const u: UnitProgress = { ...(s.progressData[scopedKey] || {}) };
      const existing = u[currentSectionId] || ({} as Partial<SectionProgress>);
      u[currentSectionId] = {
        ...existing,
        // Use score directly (in-session counter) — old code's note: avoid
        // accumulating on top of stored value, which caused 136% bugs.
        completed: score,
        score,
        total,
        maxScore: total,
        percentage: pct,
        lastUpdated: new Date().toISOString(),
        lastUpdatedMs: Date.now(),
        // version/pendingSync/completedFlag intentionally not touched here —
        // progressSync.saveProgress handles version bumping on write.
      } as SectionProgress;
      return { progressData: { ...s.progressData, [scopedKey]: u } };
    });
  },
}));

// ── External helpers ─────────────────────────────────────────────────────────

/**
 * Wipe everything held by this store (used by the auth flow on logout, after
 * progressSync's own state is cleared). Also clears the bookData cache held
 * by exerciseService via dynamic import to avoid a circular import.
 */
export async function clearExerciseStore(): Promise<void> {
  _resetSaveCache();
  useExerciseStore.setState({
    currentBookId: null,
    currentUnitId: null,
    currentSectionId: null,
    currentExerciseIndex: 0,
    score: 0,
    wrongCount: 0,
    totalExercises: 0,
    answeredIndices: new Set<number>(),
    answerHistory: [],
    isLoading: false,
    isCompletion: false,
    exerciseData: {},
    progressData: {},
    unitMediaCache: {},
  });
  // Drop the cached bookData so the next sign-in fetches fresh data.
  try {
    const { clearBookCache } = await import('../services/exerciseService');
    clearBookCache();
  } catch {
    /* non-critical */
  }
}

// ── Section completion finalizer ─────────────────────────────────────────────
//
// Mirrors the old showSectionCompletion(): when nextExercise() runs past the
// last exercise, write the final progress (with completedFlag) and force-flush
// to Firestore. Kept module-private — uses the store handle directly to avoid
// passing set/get through closures.

function _finalizeSectionProgress(): void {
  const s = useExerciseStore.getState();
  const { currentBookId, currentUnitId, currentSectionId, score, totalExercises } = s;
  if (!currentBookId || !currentUnitId || !currentSectionId) return;
  const scopedKey = _scopedProgressKey(currentBookId, currentUnitId);
  const total = totalExercises || 0;
  const correctAnswers = score;
  const percentage = total > 0 ? Math.round((correctAnswers / total) * 100) : 0;
  const is100 = total > 0 && correctAnswers === total;

  useExerciseStore.setState((cur) => {
    const u: UnitProgress = { ...(cur.progressData[scopedKey] || {}) };
    const existing = u[currentSectionId] || ({} as Partial<SectionProgress>);
    u[currentSectionId] = {
      ...existing,
      completed: correctAnswers,
      total,
      score: correctAnswers,
      maxScore: total,
      percentage,
      lastUpdated: new Date().toISOString(),
      lastUpdatedMs: Date.now(),
      completedFlag: is100,
    } as SectionProgress;
    return { progressData: { ...cur.progressData, [scopedKey]: u } };
  });

  // Force-flush so the cloud reflects the completion immediately.
  // Fire-and-forget — the UI doesn't block on this.
  void useExerciseStore.getState().saveProgress(true);
}

// ── Progress hydration listeners ─────────────────────────────────────────────

// Re-hydrate progressData whenever the background sync service merges remote
// changes into IDB. Fired by progressSync.enableRealTimeListeners() on every
// Firestore change, including the initial snapshot on login — this is what
// makes rings update on other devices and immediately after login.
if (typeof document !== 'undefined') {
  document.addEventListener('progressSynced', (e: Event) => {
    const scopedKey = (e as CustomEvent<{ unitId: string }>).detail?.unitId;
    if (!scopedKey || !auth.currentUser) return;
    progressSync.loadProgress(scopedKey)
      .then((prog) => {
        useExerciseStore.setState((s) => ({
          progressData: { ...s.progressData, [scopedKey]: prog || {} },
        }));
      })
      .catch(() => {});
  });
}

// On login (or page load for already-signed-in users), bulk-load all IDB
// progress keys so homepage rings are populated without entering any exercise.
// On logout, wipe in-memory progress/cache so stale rings don't linger.
onAuthStateChanged(auth, (user) => {
  if (!user || user.isAnonymous) {
    void clearExerciseStore();
    return;
  }
  const userPrefix = `progress:${user.uid}:`;
  progressSync.getAllProgressKeys()
    .then(async (allKeys) => {
      const scopedKeys = (allKeys as string[])
        .filter((k) => k.startsWith(userPrefix))
        .map((k) => k.slice(userPrefix.length));

      if (scopedKeys.length === 0) {
        // IDB is empty (fresh device / cleared cache) — pull directly from Firestore
        // so rings populate immediately without waiting for progressSynced events.
        const fsData = await progressSync.fetchAndHydrateFromFirestore();
        if (Object.keys(fsData).length > 0) {
          useExerciseStore.setState((s) => ({
            progressData: { ...s.progressData, ...fsData },
          }));
        }
        return;
      }

      return Promise.all(
        scopedKeys.map((scopedKey) =>
          progressSync.loadProgress(scopedKey).then((prog) => ({ scopedKey, prog }))
        )
      ).then((results) => {
        const updates: AllProgress = {};
        for (const { scopedKey, prog } of results) {
          if (prog && Object.keys(prog).length > 0) updates[scopedKey] = prog;
        }
        if (Object.keys(updates).length > 0) {
          useExerciseStore.setState((s) => ({
            progressData: { ...s.progressData, ...updates },
          }));
        }
      });
    })
    .catch(() => {});
});